/**
 * What the run is doing, once.
 *
 * This screen used to tell two stories at the same time: a badge derived from
 * the job status, and an eight-step strip that re-derived a second pipeline
 * from the same status - marking five steps "İşleniyor" simultaneously and
 * three permanently "Kullanılamıyor", including on a deployment whose worker
 * demonstrably meshes. The strip also needed horizontal scrolling at 1440px.
 *
 * There is one story now, from `status` + `current_phase`, in four or five
 * steps a customer recognises. The worker's own phase names stay available
 * under "Teknik ayrıntılar" - available, not hidden - so nothing is lost and
 * nothing is invented.
 *
 * No percentage anywhere. The engine publishes none, and a bar moving on a
 * timer is a claim about progress nobody measured.
 */

import { useEffect, useState } from "react";

import type { ReconstructionJob } from "@/api/types";
import { StatusBadge } from "@/components/ui";
import { RECONSTRUCTION_TERMS } from "@/i18n/terms";
import {
  STEP_TONES,
  elapsedOf,
  elapsedText,
  isSurfaceProfile,
  stepViews,
} from "./reconstructionState";

/** How often the elapsed reading is redrawn. A render, never a request. */
const TICK_MS = 1_000;

/**
 * The elapsed reading, counted from the server's timestamps.
 *
 * The tick only re-renders; the number is always `now - job.started_at`, so a
 * page reload keeps the true duration instead of restarting from zero, and a
 * finished job freezes because `finished_at` decides the end.
 */
export function ElapsedReading({ job }: { job: ReconstructionJob }) {
  const [now, setNow] = useState(() => Date.now());
  const live = job.status === "queued" || job.status === "running";

  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, [live]);

  const elapsed = elapsedOf(job, now);
  if (!elapsed) return null;
  return (
    <span className="faint" aria-live="off">
      {elapsedText(elapsed)}
    </span>
  );
}

/**
 * What a queued job is waiting for, and the difference between the two answers.
 *
 * Behind other work is a queue. Waiting because nothing that can run it is
 * connected is a different fact, and the customer can tell - so the product
 * says which, and says that neither needs them to do anything.
 */
export function QueueNote({
  job,
  surfaceAvailable,
}: {
  job: ReconstructionJob;
  surfaceAvailable: boolean;
}) {
  if (job.status !== "queued") return null;
  const waitingForWorker = isSurfaceProfile(job.profile_name) && !surfaceAvailable;
  return (
    <p className="muted">
      {waitingForWorker
        ? RECONSTRUCTION_TERMS.queuedNoWorkerNote
        : RECONSTRUCTION_TERMS.queuedBehindWorkNote}
    </p>
  );
}

export function RunProgress({
  job,
  surfaceAvailable,
}: {
  job: ReconstructionJob;
  surfaceAvailable: boolean;
}) {
  const views = stepViews(job);
  const active = job.status === "queued" || job.status === "running";
  // Either the worker has not reported, or it reported a phase this build
  // cannot place. Both are "we do not know where it is", and both say so
  // rather than letting the steps imply an answer.
  const unknownPhase =
    job.status === "running" && views.every((view) => view.state === "unknown");

  return (
    <section className="runprogress" aria-label="İşlem durumu">
      <ol className="runprogress__steps">
        {views.map(({ step, state }) => (
          <li
            key={step.key}
            className={`runprogress__step runprogress__step--${state}`}
            aria-current={state === "current" ? "step" : undefined}
          >
            {/*
              A word, not only a colour and not only a position. `Bekliyor`
              genuinely means waiting here: every step in this list is on the
              path this run takes, which is why the strip no longer carries
              stages the job will never reach.
            */}
            <StatusBadge tone={STEP_TONES[state]}>
              {state === "done"
                ? "Tamamlandı"
                : state === "current"
                  ? "Şu anda"
                  : state === "failed"
                    ? "Burada durdu"
                    : state === "unknown"
                      ? "Bilinmiyor"
                      : "Bekliyor"}
            </StatusBadge>
            <span className="runprogress__label">{step.label}</span>
          </li>
        ))}
      </ol>

      {unknownPhase && (
        // Running, and the worker has not said where it is. The honest answer,
        // rather than assuming the first step.
        <p className="muted">
          {RECONSTRUCTION_TERMS.phaseUnknown}. İşleyici hangi aşamada olduğunu henüz bildirmedi.
        </p>
      )}

      <QueueNote job={job} surfaceAvailable={surfaceAvailable} />

      <div className="row runprogress__foot">
        <ElapsedReading job={job} />
        {active && <span className="muted">{RECONSTRUCTION_TERMS.runsInBackground}</span>}
      </div>
    </section>
  );
}
